// PainterOps — Public estimate approval portal (P4.7)
//
// Mounted OUTSIDE AuthGate so unauthenticated homeowners can reach it via the
// /estimate/:token link that arrives by email/SMS. The estimate token is the
// only credential — pulled out of window.location, never from any auth state.
//
// Uses the edge functions estimate-public-get + estimate-respond, both
// deployed with --no-verify-jwt for the same reason.

import { useEffect, useState } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const ANON_KEY     = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

async function callEdge(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey':       ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, data };
}

function dollars(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function EstimatePortal({ token }) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null); // 'approved' | 'declined'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await callEdge('estimate-public-get', { token });
      if (cancelled) return;
      if (!res.ok) {
        setError(res.data?.error || `Error ${res.status}`);
      } else {
        setData(res.data);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function respond(decision) {
    if (decision === 'approved' && !signature.trim()) {
      setError('Please type your name to sign and approve.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await callEdge('estimate-respond', {
      token,
      decision,
      signature: decision === 'approved' ? signature.trim() : null,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    setSubmitted(decision);
  }

  if (loading) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <div style={{ color: 'var(--t3)', textAlign: 'center', padding: 40 }}>Loading…</div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={portalShell}>
        <div style={portalCard}>
          <h2 style={{ marginTop: 0 }}>Estimate unavailable</h2>
          <p style={{ color: 'var(--t2)' }}>{error}</p>
          <p style={{ color: 'var(--t3)', fontSize: 13 }}>
            Check your link — estimates expire after 7 days. If you think this is a mistake, reply to the email you received.
          </p>
        </div>
      </div>
    );
  }

  const { estimate, customer, company } = data;
  const isFinal = ['approved', 'declined', 'expired', 'voided'].includes(estimate.status);
  const lineItems = estimate.line_items || [];

  return (
    <div style={portalShell}>
      <div style={portalCard}>
        <header style={{ borderBottom: '1px solid var(--b2)', paddingBottom: 14, marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            Estimate from
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--t1)' }}>{company?.name || 'Your pool service'}</div>
          {customer?.name && (
            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--t2)' }}>
              Prepared for <strong>{customer.name}</strong>
            </div>
          )}
        </header>

        <div style={{ marginBottom: 22 }}>
          {lineItems.length === 0 ? (
            <div style={{ color: 'var(--t3)' }}>No line items.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--b2)' }}>
                  <th style={th}>Description</th>
                  <th style={{ ...th, textAlign: 'right', width: 60 }}>Qty</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>Unit</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--b1)' }}>
                    <td style={td}>{li.description}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{li.qty}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{dollars(li.unit_price)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{dollars((li.qty || 0) * (li.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 14 }}>
            <span style={{ fontSize: 14, color: 'var(--t2)' }}>Total</span>
            <span style={{ fontSize: 28, fontWeight: 700 }}>{dollars(estimate.total)}</span>
          </div>
          {estimate.deposit_amount > 0 && (
            <div style={{ marginTop: 4, textAlign: 'right', fontSize: 12, color: 'var(--t3)' }}>
              Deposit due on approval: <strong>{dollars(estimate.deposit_amount)}</strong>
            </div>
          )}
          {estimate.notes && (
            <div style={{ marginTop: 18, padding: 12, background: 'var(--s2)', borderRadius: 8, fontSize: 13, color: 'var(--t2)', whiteSpace: 'pre-wrap' }}>
              {estimate.notes}
            </div>
          )}
        </div>

        {submitted === 'approved' && (
          <div style={successBox}>
            <strong>Approved.</strong> Thanks, {signature || customer?.name || 'there'} — {company?.name || 'your pool service'} has been notified and will send an invoice shortly.
          </div>
        )}
        {submitted === 'declined' && (
          <div style={successBox}>
            <strong>Declined.</strong> We've let {company?.name || 'your pool service'} know.
          </div>
        )}

        {!submitted && isFinal && (
          <div style={{ ...successBox, background: 'var(--s2)' }}>
            This estimate is <strong>{estimate.status}</strong>. No further action is needed.
          </div>
        )}

        {!submitted && !isFinal && (
          <div style={{ borderTop: '1px solid var(--b2)', paddingTop: 18 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Type your full name to sign
            </label>
            <input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder={customer?.name || 'Your name'}
              style={input}
            />
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                style={btnApprove}
                disabled={submitting}
                onClick={() => respond('approved')}
              >
                {submitting ? 'Submitting…' : 'Approve estimate'}
              </button>
              <button
                style={btnDecline}
                disabled={submitting}
                onClick={() => respond('declined')}
              >
                Decline
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--t3)' }}>
              Expires {new Date(estimate.expires_at).toLocaleDateString()}.
            </div>
          </div>
        )}

        <footer style={{ marginTop: 28, fontSize: 11, color: 'var(--t3)', textAlign: 'center' }}>
          Powered by PainterOps
        </footer>
      </div>
    </div>
  );
}

const portalShell = {
  minHeight:       '100vh',
  display:         'flex',
  alignItems:      'flex-start',
  justifyContent:  'center',
  padding:         '32px 16px',
  background:      'var(--bg)',
};
const portalCard = {
  width:           '100%',
  maxWidth:        720,
  background:      'var(--s1)',
  borderRadius:    14,
  border:          '1px solid var(--b1)',
  padding:         '28px 28px 22px',
  boxShadow:       '0 12px 40px rgba(0,0,0,.18)',
  color:           'var(--t1)',
};
const th = { textAlign: 'left', padding: '8px 6px', fontSize: 11, color: 'var(--t3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' };
const td = { padding: '10px 6px', fontSize: 14, color: 'var(--t1)' };
const input = {
  width:        '100%',
  padding:      '10px 12px',
  fontSize:     16,
  borderRadius: 8,
  border:       '1px solid var(--b2)',
  background:   'var(--s2)',
  color:        'var(--t1)',
};
const btnApprove = {
  flex:         1,
  padding:      '12px 16px',
  fontSize:     15,
  fontWeight:   600,
  borderRadius: 9,
  border:       'none',
  cursor:       'pointer',
  background:   'var(--teal)',
  color:        '#001216',
};
const btnDecline = {
  padding:      '12px 16px',
  fontSize:     15,
  fontWeight:   600,
  borderRadius: 9,
  border:       '1px solid var(--b2)',
  cursor:       'pointer',
  background:   'transparent',
  color:        'var(--t2)',
};
const successBox = {
  marginTop:    18,
  padding:      14,
  background:   'var(--teal-t)',
  border:       '1px solid var(--teal)',
  borderRadius: 9,
  color:        'var(--t1)',
  fontSize:     14,
};
const errorBox = {
  marginTop:    10,
  padding:      10,
  background:   'var(--rose-t)',
  border:       '1px solid var(--rose)',
  borderRadius: 8,
  color:        'var(--t1)',
  fontSize:     13,
};
